import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Lock, Shield, KeyRound, ArrowRight, CheckCircle2 } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const { login, verifyMfa, mfaRequired } = useAuth();
  const [email, setEmail] = useState<string>('admin@mywatchman.com');
  const [password, setPassword] = useState<string>('password123');
  const [totpCode, setTotpCode] = useState<string>('123456');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const ok = await verifyMfa(totpCode);
      if (!ok) {
        setError('Invalid TOTP verification code. Please enter 6 digits.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at top center, #0f172a 0%, #060911 100%)',
        padding: '20px',
      }}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '420px',
          padding: '32px',
          boxShadow: 'var(--shadow-lg)',
          borderColor: 'var(--border-dim)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              margin: '0 auto 12px',
              boxShadow: 'var(--shadow-glow)',
            }}
          >
            <Shield size={24} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)' }}>
            MyWatchman Admin
          </h2>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Platform Operator Console
          </p>
        </div>

        {error && (
          <div
            style={{
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger-border)',
              color: 'var(--danger-text)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              marginBottom: '16px',
              fontSize: '12.5px',
            }}
          >
            {error}
          </div>
        )}

        {!mfaRequired ? (
          /* Step 1: Email + Password */
          <form onSubmit={handleCredentialsSubmit}>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Admin Email
              </label>
              <input
                type="email"
                className="input"
                style={{ width: '100%' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Password
              </label>
              <input
                type="password"
                className="input"
                style={{ width: '100%' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              <span>{isLoading ? 'Verifying...' : 'Next: 2FA Challenge'}</span>
              <ArrowRight size={14} />
            </button>
          </form>
        ) : (
          /* Step 2: Mandatory MFA (TOTP) Challenge Step */
          <form onSubmit={handleMfaSubmit}>
            <div
              style={{
                background: 'rgba(56, 189, 248, 0.08)',
                border: '1px solid rgba(56, 189, 248, 0.2)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 12px',
                fontSize: '12px',
                color: 'var(--primary-light)',
                marginBottom: '16px',
              }}
            >
              🔐 <strong>MFA Challenge Required:</strong> Enter the 6-digit verification code from your authenticator app.
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                TOTP 6-Digit Code
              </label>
              <input
                type="text"
                className="input"
                style={{ width: '100%', fontSize: '18px', textAlign: 'center', letterSpacing: '0.25em' }}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isLoading || totpCode.length < 6}
            >
              <span>{isLoading ? 'Authenticating...' : 'Verify & Enter Console'}</span>
            </button>
          </form>
        )}

        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '11px', color: 'var(--text-dim)' }}>
          Session tokens auto-refresh. Privileged actions restricted by role permissions.
        </div>
      </div>
    </div>
  );
};
